import React, { useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonLoading, FormCheck, FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { useWindowSize } from "go-core/components/useWindowSize";
import { FormDirty } from "go-form/components/FormDirty";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { adminApi } from "../../../../services/Api/Admin/adminApi";
import {
	OrganizationApi,
	OrganizationFeatureApi,
	OrganizationFeatureFormApi,
	OrganizationFeatureFormFeatureApi,
	UserRole,
} from "../../../../services/Api/types";
import { OrganizationPreviewRequests } from "../Admin/Organization/services/types";

interface Props {
	features: OrganizationFeatureApi[];
	organization: OrganizationApi;
	info: any;
	requests: OrganizationPreviewRequests;
	context: string;
	onSuccess: () => void;
	onHide: () => void;
	areAllowed: (contexts: Record<string, UserRole[]>) => boolean;
}

const parseFormDefaultValues = (features: OrganizationFeatureApi[], info: any): OrganizationFeatureFormFeatureApi[] => {
	const result: OrganizationFeatureFormFeatureApi[] = [];

	info.types.forEach((type) => {
		const feature = features.find(({ name }) => type.toString() === name);
		if (!feature) {
			result.push({
				potential: false,
				sold_by: null,
				sold_by_partner_id: null,
				activated_by: null,
				activated_by_partner_id: null,
				notes: "",
				name: type,
			});
		} else {
			result.push({
				potential: feature.potential,
				sold_by: feature.sold_by_partner ?? null,
				sold_by_partner_id: feature.sold_by_partner_id ?? null,
				activated_by: feature.activated_by_partner ?? null,
				activated_by_partner_id: feature.activated_by_partner_id ?? null,
				notes: feature.notes,
				name: type,
			});
		}
	});

	return result;
};

const OrganizationFeaturesTableForm = (props: Props) => {
	const { t } = useTranslation("admin");
	const form = useForm<OrganizationFeatureFormApi>({
		criteriaMode: "all",
		defaultValues: {
			features: parseFormDefaultValues(props.features, props.info),
		},
	});
	const {
		formState,
		formState: { errors },
		control,
		watch,
		setValue,
		handleSubmit,
		setError,
		reset,
		register,
	} = form;
	const [loading, setLoading] = useState(false);
	const { addFlash, addSuccessFlash } = useFlash();
	const isMobile = useWindowSize().isMobile;

	const searchPartner = (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		return adminApi.getAdminPartnersSearchSelect(search, params, {
			cancelToken: options?.token,
		});
	};

	const getParsedFormData = (data: OrganizationFeatureFormApi) => {
		return data.features.filter((item) => item.potential);
	};

	const onSubmit = handleSubmit(async (data) => {
		if (!props.requests.updateFeatures) {
			return;
		}
		setLoading(true);
		const newData = { features: getParsedFormData(data) };
		try {
			const res = await props.requests.updateFeatures(props.organization.id, newData);
			reset({
				features: [...parseFormDefaultValues(res, props.info)],
			});
			addSuccessFlash(t("lib:common.flash.saved"));
			props.onSuccess();
		} catch (err) {
			handleError.form(err, setError, addFlash);
		}
		setLoading(false);
	});

	const isAdminContext = () => {
		return props.areAllowed({
			ADMIN: [UserRole.ADMIN, UserRole.OFFICE, UserRole.CS_TL, UserRole.SALE_TL, UserRole.CSV_TL],
			PARTNER: [UserRole.ADMIN, UserRole.OFFICE, UserRole.CS_TL, UserRole.SALE_TL, UserRole.CSV_TL],
			SUPPORT: [UserRole.ADMIN, UserRole.OFFICE, UserRole.CS_TL, UserRole.SALE_TL, UserRole.CSV_TL],
		});
	};

	return (
		<FormDirty formState={formState} onSubmit={onSubmit}>
			<Modal.Body>
				<fieldset className="form-group">
					<div className={`${isMobile ? "table-responsive" : ""}`}>
						<table className="table table-form">
							<thead>
								<tr>
									<th className="action" />
									<th style={{ minWidth: isMobile ? "200px" : "100px" }}>
										{t("lib:common.word.name")}
									</th>
									{isAdminContext() && (
										<>
											<th style={{ minWidth: isMobile ? "200px" : "100px" }}>Przekonał</th>
											<th style={{ minWidth: isMobile ? "200px" : "100px" }}>Wdrożył</th>
										</>
									)}
								</tr>
							</thead>
							<tbody>
								{props.info.types.map((feature, index) => {
									const correspondingFeature = props.features.find(({ name }) => feature === name);

									return (
										<tr key={feature}>
											<td className="action" />
											<td>
												<FormCheck
													name={`features.${index}.potential`}
													errors={errors}
													register={register}
													label={feature}
													type="switch"
													disabled={correspondingFeature?.enabled}
												/>
												<small>Wdrożenie niskoprogowe, prezentacja oferty</small>
												{watch(`features.${index}.potential`) && (
													<>
														<hr />
														<FormInput
															name={`features.${index}.notes`}
															as={"textarea"}
															rows={1}
															errors={errors}
															register={register}
															placeholder={"Dodaj notatke ... "}
														/>
													</>
												)}
											</td>
											{isAdminContext() && (
												<>
													<td>
														<FormSelectGroup
															errors={errors}
															control={control}
															loadOptions={searchPartner}
															name={`features.${index}.sold_by_partner_id`}
															getOptionValue={(option) => option.id}
															getOptionLabel={(option) => option.name}
															data-testid={`features.${index}.sold_by_partner_id`}
															defaultValue={{
																name: watch(`features.${index}.sold_by`)?.name || null,
																id:
																	watch(`features.${index}.sold_by_partner_id`) ||
																	null,
															}}
															onChange={(_, object) =>
																setValue(`features.${index}.sold_by.name`, object.name)
															}
														/>
													</td>

													<td>
														<FormSelectGroup
															errors={errors}
															control={control}
															loadOptions={searchPartner}
															name={`features.${index}.activated_by_partner_id`}
															getOptionValue={(option) => option.id}
															getOptionLabel={(option) => option.name}
															data-testid={`features.${index}.activated_by_partner_id`}
															defaultValue={{
																name:
																	watch(`features.${index}.activated_by`)?.name ||
																	null,
																id:
																	watch(
																		`features.${index}.activated_by_partner_id`
																	) || null,
															}}
															onChange={(_, object) =>
																setValue(
																	`features.${index}.activated_by.name`,
																	object.name
																)
															}
														/>
													</td>
												</>
											)}
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				</fieldset>
			</Modal.Body>
			<Modal.Footer>
				<ButtonLoading loading={loading} type={"submit"}>
					{t("lib:common.action.save")}
				</ButtonLoading>
				<Button variant="light" onClick={props.onHide}>
					{t("common.action.cancel", { ns: "lib" })}
				</Button>
			</Modal.Footer>
		</FormDirty>
	);
};

export default OrganizationFeaturesTableForm;
